/**
 * MyWritingContext — global state for the My Writing feature.
 *
 * Stories are persisted to localStorage (via useLocalStore) rather than kept
 * in memory only — unlike some sibling features, losing a draft here is the
 * one failure mode writers can't shrug off.
 */

import React, { createContext, useContext, useCallback, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useLocalStore } from '@/lib/useLocalStore';
import { useAuth } from '@/lib/useAuth';
import { SEED_STORIES } from '../constants/seedStories';
import { DEFAULT_CHAPTER_TITLE } from '../constants';

const MyWritingContext = createContext(undefined);

const makeId = (prefix) => `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

function normalizeManuscript(raw) {
  const chapters = raw.chapters ?? (raw.content != null ? [{ id: `${raw.id}_chapter_1`, title: 'Chapter 1', content: raw.content, createdAt: raw.createdAt, updatedAt: raw.updatedAt }] : []);
  return {
    ...raw,
    id: raw.id ?? raw.manuscriptId,
    title: raw.title ?? raw.name ?? 'Untitled Story',
    description: raw.description ?? '',
    genre: raw.genre ?? 'Fiction',
    tags: raw.tags ?? [],
    status: raw.status ?? 'draft',
    visibility: raw.visibility ?? 'private',
    goal: raw.goal ?? null,
    isFavorite: raw.isFavorite ?? false,
    createdAt: raw.createdAt ? new Date(raw.createdAt).getTime() : Date.now(),
    updatedAt: raw.updatedAt ? new Date(raw.updatedAt).getTime() : Date.now(),
    chapters: chapters.map((chapter, index) => ({
      ...chapter,
      id: chapter.id ?? `${raw.id}_chapter_${index + 1}`,
      title: chapter.title ?? `Chapter ${index + 1}`,
      content: chapter.content ?? '',
    })),
  };
}

function makeChapter(title = DEFAULT_CHAPTER_TITLE) {
  const now = Date.now();
  return {
    id: makeId('ch'),
    title,
    content: '',
    createdAt: now,
    updatedAt: now,
  };
}

export function MyWritingProvider({ children }) {
  const location = useLocation();
  const { user, getManuscripts, getManuscript, createManuscript: createManuscriptApi, updateManuscript: updateManuscriptApi, deleteManuscript: deleteManuscriptApi, createChapter: createChapterApi, updateChapter: updateChapterApi, deleteChapter: deleteChapterApi } = useAuth();
  const [stories, setStories] = useLocalStore('sr-my-writing-stories', SEED_STORIES);

  useEffect(() => {
    if (!user) return undefined;
    let active = true;
    getManuscripts()
      .then((payload) => {
        if (!active) return;
        const result = payload?.data ?? payload;
        const list = result?.manuscripts || result?.items || result?.results || (Array.isArray(result) ? result : []);
        setStories(list.map(normalizeManuscript).filter((story) => story.id));
      })
      .catch((error) => console.error('[Manuscripts API] Failed to load manuscripts:', error));
    return () => { active = false; };
  }, [getManuscripts, setStories, user]);

  useEffect(() => {
    const manuscriptId = location.pathname.match(/\/app\/studio\/([^/]+)/)?.[1];
    if (!user || !manuscriptId || manuscriptId === 'new') return undefined;
    let active = true;
    getManuscript(manuscriptId)
      .then((payload) => {
        if (!active) return;
        const result = payload?.data?.manuscript || payload?.data || payload?.manuscript || payload;
        if (!result || typeof result !== 'object') return;
        const remoteStory = normalizeManuscript(result);
        setStories((previous) => previous.some((story) => story.id === remoteStory.id)
          ? previous.map((story) => story.id === remoteStory.id ? { ...story, ...remoteStory } : story)
          : [remoteStory, ...previous]);
      })
      .catch((error) => console.error('[Manuscripts API] Failed to load manuscript:', error));
    return () => { active = false; };
  }, [getManuscript, location.pathname, setStories, user]);

  const getStory = useCallback(
    (storyId) => stories.find((s) => s.id === storyId) ?? null,
    [stories]
  );

  const createStory = useCallback(async ({ title, description, genre, goal, tags }) => {
    const now = Date.now();
    const story = {
      id: makeId('story'),
      title: title?.trim() || 'Untitled Story',
      description: description?.trim() ?? '',
      genre: genre ?? 'Fiction',
      tags: tags ?? [],
      cover: { type: 'typographic' },
      status: 'draft',
      visibility: 'private',
      goal: goal ?? null,
      isFavorite: false,
      createdAt: now,
      updatedAt: now,
      chapters: [makeChapter('Chapter 1')],
    };
    const payload = await createManuscriptApi({ title: story.title, description: story.description, genre: story.genre, goal: story.goal, tags: story.tags, status: story.status, visibility: story.visibility });
    const result = payload?.data?.manuscript || payload?.data || payload?.manuscript || payload;
    const createdStory = normalizeManuscript({ ...story, ...result });
    setStories((prev) => [createdStory, ...prev]);
    return createdStory;
  }, [createManuscriptApi, setStories]);

  const updateStory = useCallback(async (storyId, updates) => {
    const payload = await updateManuscriptApi(storyId, updates);
    const result = payload?.data?.manuscript || payload?.data || payload?.manuscript || {};
    setStories((prev) =>
      prev.map((s) => (s.id === storyId ? { ...s, ...updates, ...result, updatedAt: result.updatedAt ? new Date(result.updatedAt).getTime() : Date.now() } : s))
    );
    return result;
  }, [setStories, updateManuscriptApi]);

  const touchStory = useCallback((storyId) => {
    setStories((prev) =>
      prev.map((s) => (s.id === storyId ? { ...s, updatedAt: Date.now() } : s))
    );
  }, [setStories]);

  const deleteStory = useCallback(async (storyId) => {
    await deleteManuscriptApi(storyId);
    setStories((prev) => prev.filter((s) => s.id !== storyId));
  }, [deleteManuscriptApi, setStories]);

  const duplicateStory = useCallback((storyId) => {
    let copy = null;
    setStories((prev) => {
      const original = prev.find((s) => s.id === storyId);
      if (!original) return prev;
      const now = Date.now();
      copy = {
        ...original,
        id: makeId('story'),
        title: `${original.title} (Copy)`,
        status: 'draft',
        visibility: 'private',
        createdAt: now,
        updatedAt: now,
        chapters: original.chapters.map((ch) => ({ ...ch, id: makeId('ch') })),
      };
      return [copy, ...prev];
    });
    return copy;
  }, [setStories]);

  const toggleFavorite = useCallback((storyId) => {
    setStories((prev) =>
      prev.map((s) => (s.id === storyId ? { ...s, isFavorite: !s.isFavorite } : s))
    );
  }, [setStories]);

  const archiveStory = useCallback((storyId) => {
    updateStory(storyId, { status: 'archived' });
  }, [updateStory]);

  const restoreStory = useCallback((storyId) => {
    updateStory(storyId, { status: 'draft' });
  }, [updateStory]);

  const publishStory = useCallback((storyId, visibility = 'published') => {
    updateStory(storyId, {
      status: visibility === 'published' ? 'published' : 'completed',
      visibility,
    });
  }, [updateStory]);

  const unpublishStory = useCallback((storyId) => {
    updateStory(storyId, { status: 'draft', visibility: 'private' });
  }, [updateStory]);

  const addChapter = useCallback(async (storyId, title) => {
    const story = stories.find((item) => item.id === storyId);
    if (!story) return null;
    const chapterData = {
      title: title || `Chapter ${story.chapters.length + 1}`,
      content: '',
      position: story.chapters.length,
    };
    const payload = await createChapterApi(storyId, chapterData);
    const result = payload?.data?.chapter || payload?.data || payload?.chapter || payload;
    const chapter = { ...chapterData, ...result, id: result.id ?? result.chapterId ?? makeId('ch') };
    setStories((prev) => prev.map((item) => (
      item.id === storyId ? { ...item, chapters: [...item.chapters, chapter], updatedAt: Date.now() } : item
    )));
    return chapter;
  }, [createChapterApi, stories, setStories]);

  const updateChapter = useCallback(async (storyId, chapterId, updates) => {
    const story = stories.find((item) => item.id === storyId);
    if (!story) return;
    const currentChapter = story.chapters.find((chapter) => chapter.id === chapterId);
    if (!currentChapter) return;
    const position = story.chapters.findIndex((chapter) => chapter.id === chapterId);
    const chapterData = { title: updates.title ?? currentChapter.title, content: updates.content ?? currentChapter.content, position };
    const payload = await updateChapterApi(chapterId, chapterData);
    const result = payload?.data?.chapter || payload?.data || payload?.chapter || {};
    const updatedChapter = { ...currentChapter, ...chapterData, ...result, updatedAt: Date.now() };
    setStories((prev) => prev.map((item) => (
      item.id === storyId ? { ...item, chapters: item.chapters.map((chapter) => chapter.id === chapterId ? updatedChapter : chapter), updatedAt: Date.now() } : item
    )));
  }, [stories, setStories, updateChapterApi]);

  const deleteChapter = useCallback(async (storyId, chapterId) => {
    await deleteChapterApi(chapterId);
    setStories((prev) =>
      prev.map((s) =>
        s.id === storyId
          ? { ...s, chapters: s.chapters.filter((ch) => ch.id !== chapterId), updatedAt: Date.now() }
          : s
      )
    );
  }, [deleteChapterApi, setStories]);

  const duplicateChapter = useCallback((storyId, chapterId) => {
    setStories((prev) =>
      prev.map((s) => {
        if (s.id !== storyId) return s;
        const idx = s.chapters.findIndex((ch) => ch.id === chapterId);
        if (idx === -1) return s;
        const original = s.chapters[idx];
        const copy = { ...original, id: makeId('ch'), title: `${original.title} (Copy)`, updatedAt: Date.now() };
        const chapters = [...s.chapters];
        chapters.splice(idx + 1, 0, copy);
        return { ...s, chapters, updatedAt: Date.now() };
      })
    );
  }, [setStories]);

  const reorderChapters = useCallback((storyId, fromIndex, toIndex) => {
    setStories((prev) =>
      prev.map((s) => {
        if (s.id !== storyId) return s;
        const chapters = [...s.chapters];
        const [moved] = chapters.splice(fromIndex, 1);
        chapters.splice(toIndex, 0, moved);
        return { ...s, chapters, updatedAt: Date.now() };
      })
    );
  }, [setStories]);

  const importStory = useCallback(async ({ title, content, genre }) => {
    const now = Date.now();
    const story = {
      id: makeId('story'),
      title: title?.trim() || 'Imported Story',
      description: '',
      genre: genre ?? 'Fiction',
      tags: [],
      cover: { type: 'typographic' },
      status: 'draft',
      visibility: 'private',
      goal: null,
      isFavorite: false,
      createdAt: now,
      updatedAt: now,
      chapters: [{ ...makeChapter('Chapter 1'), content }],
    };
    const payload = await createManuscriptApi({ title: story.title, description: story.description, genre: story.genre, status: story.status, visibility: story.visibility, chapters: story.chapters });
    const result = payload?.data?.manuscript || payload?.data || payload?.manuscript || payload;
    const importedStory = normalizeManuscript({ ...story, ...result });
    setStories((prev) => [importedStory, ...prev]);
    return importedStory;
  }, [createManuscriptApi, setStories]);

  const value = useMemo(() => ({
    stories,
    getStory,
    createStory,
    updateStory,
    touchStory,
    deleteStory,
    duplicateStory,
    toggleFavorite,
    archiveStory,
    restoreStory,
    publishStory,
    unpublishStory,
    addChapter,
    updateChapter,
    deleteChapter,
    duplicateChapter,
    reorderChapters,
    importStory,
  }), [
    stories, getStory, createStory, updateStory, touchStory, deleteStory, duplicateStory,
    toggleFavorite, archiveStory, restoreStory, publishStory, unpublishStory,
    addChapter, updateChapter, deleteChapter, duplicateChapter, reorderChapters, importStory,
  ]);

  return <MyWritingContext.Provider value={value}>{children}</MyWritingContext.Provider>;
}

export function useMyWriting() {
  const context = useContext(MyWritingContext);
  if (!context) {
    throw new Error('useMyWriting must be used within MyWritingProvider');
  }
  return context;
}
